import Button from '@jusbrasil-rc/button'
import { useComposedRefs } from '@jusbrasil-web/shared-hooks'
import classNames from 'classnames/bind'
import { forwardRef, ReactNode, useContext, useEffect, useRef, useState } from 'react'

import { SnippetCarouselContext } from './context'
import styles from './snippet-carousel.module.scss'

const cx = classNames.bind(styles)

export interface SnippetCarouselProps {
  children: ReactNode
  maxItemWidth: number
  offsetScroll?: number
  className?: string
  onArrowClick?: (direction: 1 | -1) => void
  onActivePageIndex?: (activePageIndex: number) => void
  __parentWrapperTestMode?: boolean
}

interface State {
  showLeft: boolean
  showRight: boolean
}

export const SnippetCarousel = forwardRef<HTMLDivElement, SnippetCarouselProps>(
  function SnippetCarousel(props, forwardedRef) {
    const {
      children,
      className,
      maxItemWidth,
      onArrowClick,
      onActivePageIndex,
      offsetScroll = 0,
      __parentWrapperTestMode = false,
    } = props
    const [state, setState] = useState<State>({ showLeft: false, showRight: false })
    const ref = useRef<HTMLDivElement>(null)

    const ctx = useContext(SnippetCarouselContext)
    const refs = useComposedRefs(forwardedRef, ctx.scrollRef, ref)

    useEffect(() => {
      const element = ref.current
      if (!element) return

      const setArrowsState = () => {
        const contentRight = element.getBoundingClientRect().right || 0
        const lastItemRight = element.lastElementChild?.getBoundingClientRect().right || 0
        const showLeft = element.scrollLeft !== 0
        const showRight = Math.round(lastItemRight) > Math.round(contentRight)
        // [perf] avoid re-render unnecessary when scrolling
        if (showLeft !== state.showLeft || showRight !== state.showRight) {
          setState({ showLeft, showRight })
        }
      }

      setArrowsState()

      const onScroll = () => {
        setArrowsState()
        onActivePageIndex?.(ctx.activePageIndex)
      }

      element.addEventListener('scroll', onScroll)
      return () => {
        element.removeEventListener('scroll', onScroll)
      }
    }, [ctx.activePageIndex, onActivePageIndex, ref, state])

    function scrollDirection(multiplier: number) {
      if (ref.current) {
        const currentItem = Math.round(ref.current.scrollLeft / maxItemWidth)
        const scrollLeft = Math.max((currentItem + multiplier) * maxItemWidth + offsetScroll, 0)
        ref.current.scrollLeft = scrollLeft
      }
    }

    return (
      <div className={cx('SnippetCarousel', className)} ref={forwardedRef}>
        {(__parentWrapperTestMode || state.showLeft) && (
          <div
            className={`${styles['SnippetCarousel-arrow']} ${styles['SnippetCarousel-arrow--left']}`}
          >
            <Button
              type="button"
              color="white"
              isIcon
              isCircle
              onClick={() => {
                scrollDirection(-1)
                onArrowClick?.(-1)
              }}
              data-testid="back-button"
            >
              <span className="icon icon--lg icon-chevron-left" />
            </Button>
          </div>
        )}

        <div
          className={styles['SnippetCarousel-content']}
          data-testid="snippet-carousel-content"
          ref={refs}
        >
          {children}
        </div>

        {(__parentWrapperTestMode || state.showRight) && (
          <div
            className={`${styles['SnippetCarousel-arrow']} ${styles['SnippetCarousel-arrow--right']}`}
          >
            <Button
              type="button"
              color="white"
              isIcon
              isCircle
              onClick={() => {
                scrollDirection(1)
                onArrowClick?.(1)
              }}
              data-testid="next-button"
            >
              <span className="icon icon--lg icon-chevron-right" />
            </Button>
          </div>
        )}
      </div>
    )
  }
)
